import React, { useEffect, useRef, useState } from 'react';

/* ── Demo scenarios the agent cycles through ── */
const SCENARIOS = [
  {
    prompt: 'Launch a TikTok campaign for our sneaker drop — $50/day, women 18–30 in the US',
    platform: 'TikTok',
    pColor: '#f0264e',
    fields: [
      { k: 'Objective',    v: 'Traffic' },
      { k: 'Daily budget', v: '$50' },
      { k: 'Audience',     v: 'Women · 18–30 · United States' },
      { k: 'Placement',    v: 'TikTok Feed · Automatic' },
    ],
    done: 'Campaign created & launched via TikTok API',
  },
  {
    prompt: 'Create a Meta lead-gen campaign for our webinar — €30/day, marketers in France',
    platform: 'Meta',
    pColor: '#1877f2',
    fields: [
      { k: 'Objective',    v: 'Lead generation' },
      { k: 'Daily budget', v: '€30' },
      { k: 'Audience',     v: 'Interest: Marketing · France' },
      { k: 'Placement',    v: 'Feed · Stories · Reels' },
    ],
    done: 'Campaign created & launched via Meta Graph API',
  },
];

/* ── Animated agent conversation (types, fills the form, launches) ── */
function AgentDemo({ active }) {
  const [scn, setScn]         = useState(0);
  const [typed, setTyped]     = useState(0);
  const [phase, setPhase]     = useState('typing'); // typing → thinking → card → done
  const [fieldIdx, setFieldIdx] = useState(0);

  const s = SCENARIOS[scn];

  useEffect(() => {
    if (!active) return;
    let t;
    if (phase === 'typing') {
      if (typed < s.prompt.length) {
        t = setTimeout(() => setTyped(n => n + 1), 26);
      } else {
        t = setTimeout(() => setPhase('thinking'), 500);
      }
    } else if (phase === 'thinking') {
      t = setTimeout(() => { setPhase('card'); setFieldIdx(0); }, 1100);
    } else if (phase === 'card') {
      if (fieldIdx < s.fields.length) {
        t = setTimeout(() => setFieldIdx(n => n + 1), 380);
      } else {
        t = setTimeout(() => setPhase('done'), 600);
      }
    } else if (phase === 'done') {
      t = setTimeout(() => {
        setScn(i => (i + 1) % SCENARIOS.length);
        setTyped(0);
        setFieldIdx(0);
        setPhase('typing');
      }, 3200);
    }
    return () => clearTimeout(t);
  }, [active, phase, typed, fieldIdx, s.prompt.length, s.fields.length]);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(129,140,248,0.3)',
      borderRadius: 18,
      overflow: 'hidden',
      boxShadow: '0 24px 64px rgba(0,0,0,0.4), 0 0 80px rgba(129,140,248,0.08)',
      minHeight: 380,
    }}>
      {/* Window bar */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.03)' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>AI Campaign Agent · OneBotAds</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#818CF8', fontWeight: 700 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#818CF8', boxShadow: '0 0 6px #818CF8' }} /> ONLINE
        </span>
      </div>

      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* User message typing */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(129,140,248,0.4)', borderRadius: '14px 14px 3px 14px', padding: '10px 14px', maxWidth: '85%', fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.55, minHeight: 38 }}>
            {s.prompt.slice(0, typed)}
            {phase === 'typing' && <span style={{ borderRight: '2px solid #818CF8', marginLeft: 1, animation: 'aiBlink 0.8s step-end infinite' }} />}
          </div>
        </div>

        {/* Agent thinking */}
        {(phase === 'thinking' || phase === 'card' || phase === 'done') && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 28, height: 28, borderRadius: 9, background: '#221C51', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13, boxShadow: '0 0 16px rgba(129,140,248,0.4)' }}>✦</div>
            {phase === 'thinking' ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: '3px 14px 14px 14px', border: '1px solid rgba(255,255,255,0.07)' }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#818CF8', animation: `aiBounce 1.1s ${i * 0.18}s ease-in-out infinite` }} />
                ))}
              </div>
            ) : (
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px 14px 14px 14px', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>Got it — building your campaign:</span>
                  <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 10, fontWeight: 800, background: `${s.pColor}22`, color: s.pColor === '#f0264e' ? '#ff5e7e' : '#6aa8ff', border: `1px solid ${s.pColor}55` }}>{s.platform}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {s.fields.slice(0, fieldIdx).map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(129,140,248,0.25)', borderRadius: 8, animation: 'aiFieldIn 0.3s ease both' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#818CF8', textTransform: 'uppercase', letterSpacing: '0.07em', width: 86, flexShrink: 0 }}>{f.k}</span>
                      <span style={{ fontSize: 12, color: '#fff' }}>{f.v}</span>
                      <span style={{ marginLeft: 'auto', color: '#818CF8', fontSize: 11 }}>✓</span>
                    </div>
                  ))}
                </div>
                {phase === 'done' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '9px 12px', background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.35)', borderRadius: 8, animation: 'aiFieldIn 0.35s ease both' }}>
                    <span style={{ fontSize: 13 }}>🚀</span>
                    <span style={{ fontSize: 12, color: '#818CF8', fontWeight: 700 }}>{s.done}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── MCP terminal mockup ── */
const MCP_LINES = [
  { p: '>', text: 'claude connected to OneBotAds MCP servers', dim: true },
  { p: '$', tool: 'meta.list_campaigns', result: '✓ 12 campaigns returned' },
  { p: '$', tool: 'tiktok.get_insights --last 7d', result: '✓ CTR 3.8% · spend $612' },
  { p: '$', tool: 'meta.update_budget --id 8841 --daily 75', result: '✓ budget updated' },
  { p: '$', tool: 'google.create_campaign --objective SALES', result: '✓ campaign live' },
];

function McpTerminal({ active }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (shown >= MCP_LINES.length) return;
    const t = setTimeout(() => setShown(n => n + 1), shown === 0 ? 300 : 800);
    return () => clearTimeout(t);
  }, [active, shown]);

  return (
    <div style={{ background: '#120E26', border: '1px solid rgba(129,140,248,0.25)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.4)' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 6, fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>mcp — any AI agent</span>
      </div>
      <div style={{ padding: '14px 16px', fontFamily: "'Consolas','Monaco',monospace", fontSize: 12, lineHeight: 2 }}>
        {MCP_LINES.slice(0, shown).map((l, i) => (
          <div key={i} style={{ animation: 'aiFieldIn 0.3s ease both' }}>
            <span style={{ color: l.dim ? 'rgba(255,255,255,0.3)' : '#818CF8' }}>{l.p} </span>
            {l.dim ? (
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l.text}</span>
            ) : (
              <>
                <span style={{ color: '#818CF8' }}>{l.tool}</span>
                <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 10 }}>{l.result}</span>
              </>
            )}
          </div>
        ))}
        {shown < MCP_LINES.length && <span style={{ color: '#818CF8', animation: 'aiBlink 0.8s step-end infinite' }}>▋</span>}
      </div>
    </div>
  );
}

const MCP_SERVERS = [
  { name: 'meta-mcp-server',   color: '#1877f2', tools: ['list_campaigns', 'create_campaign', 'get_insights', 'update_status', 'update_budget', 'list_adsets'] },
  { name: 'tiktok-mcp-server', color: '#f0264e', tools: ['list_campaigns', 'create_campaign', 'get_insights', 'update_status', 'update_budget', 'list_adsets'] },
  { name: 'google-mcp-server', color: '#34a853', tools: ['list_campaigns', 'create_campaign', 'get_insights', 'update_status', 'update_budget', 'list_adsets'] },
];

const CREATIVE_TOOLS = [
  { name: 'Imagen 3',        desc: 'Text-to-image ad creatives' },
  { name: 'Veo 3',           desc: 'Text-to-video ads' },
  { name: 'Higgsfield Soul', desc: 'Cinematic image generation' },
  { name: 'Higgsfield DoP',  desc: 'Image-to-video motion' },
];

/* ── Section ── */
const AIShowcase = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="ai-agent" ref={ref} style={{ background: '#0F0B2D', padding: '120px 24px', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes aiBlink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes aiBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
        @keyframes aiFieldIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes aiGlow { 0%,100%{opacity:.5} 50%{opacity:1} }
        .mcp-card { transition: all 0.25s ease; }
        .mcp-card:hover { transform: translateY(-4px); border-color: rgba(129,140,248,0.5) !important; }
      `}</style>

      {/* AI-generated backdrop — Higgsfield (Z Image), dimmed and edge-faded
          into the section navy so the content stays readable */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://res.cloudinary.com/dxtj7sllk/image/upload/v1781266396/fnrlwtjyjjmlvxpohtmt.png)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.55, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #0F0B2D 0%, rgba(15,11,45,0.45) 20%, rgba(15,11,45,0.45) 80%, #0F0B2D 100%)', pointerEvents: 'none' }} />

      {/* Faint light */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 70% 50% at 70% 0%, rgba(255,255,255,0.045) 0%, transparent 60%)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ marginBottom: 72, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)', transition: 'all 0.7s ease' }}>
          <div className="mono-label" style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 22 }}>
            <span style={{ color: '#818CF8' }}>/</span> AI Agent
          </div>
          <h2 style={{ fontSize: 'clamp(32px,4.5vw,54px)', fontWeight: 700, color: '#fff', letterSpacing: '-0.035em', marginBottom: 16, lineHeight: 1.08, maxWidth: 760 }}>
            Tell the agent.{' '}
            <em className="serif-accent" style={{ color: '#818CF8' }}>It builds the campaign.</em>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', maxWidth: 540, lineHeight: 1.7 }}>
            One sentence in plain language — the agent extracts your objective, budget, audience and placements, fills all 26 campaign fields, and launches on the real platform API.
          </p>
        </div>

        {/* Agent demo + copy */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 48, alignItems: 'center', marginBottom: 90, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(32px)', transition: 'all 0.7s 0.15s ease' }}>
          <AgentDemo active={visible} />

          <div>
            <h3 style={{ fontSize: 'clamp(20px,2.5vw,28px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 16, lineHeight: 1.3 }}>
              From conversation to live ad — no forms, no tabs
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                'Understands objectives, budgets, audiences & schedules in natural language',
                'Auto-fills all 26 campaign form fields with structured actions',
                'Agentic submit — reviews, validates, then launches for real',
                'Works across all 6 connected ad platforms',
              ].map((b, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 14.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  <span style={{ width: 20, height: 20, borderRadius: 6, background: 'rgba(129,140,248,0.18)', border: '1px solid rgba(129,140,248,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#818CF8', flexShrink: 0, marginTop: 2 }}>✓</span>
                  {b}
                </li>
              ))}
            </ul>

            {/* Creative studio chips */}
            <div style={{ padding: '16px 18px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                + AI Creative Studio
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {CREATIVE_TOOLS.map((t, i) => (
                  <span key={i} title={t.desc} style={{ padding: '6px 13px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: 'rgba(110,231,183,0.07)', border: '1px solid rgba(110,231,183,0.25)', color: '#818CF8' }}>
                    {t.name}
                  </span>
                ))}
              </div>
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.4)', margin: '12px 0 0', lineHeight: 1.6 }}>
                Generate ad images and videos from a text prompt — saved to your Ad Library, ready to attach to any campaign.
              </p>
            </div>
          </div>
        </div>

        {/* MCP sub-section */}
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(32px)', transition: 'all 0.7s 0.3s ease' }}>
          <div style={{ marginBottom: 48 }}>
            <div className="mono-label" style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 20 }}>
              <span style={{ color: '#818CF8' }}>/</span> Open Agent Protocol
            </div>
            <h3 style={{ fontSize: 'clamp(24px,3vw,38px)', fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', marginBottom: 12, lineHeight: 1.12, maxWidth: 640 }}>
              Your ads, controllable by any AI agent —{' '}
              <em className="serif-accent" style={{ color: '#818CF8' }}>via MCP.</em>
            </h3>
            <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.45)', maxWidth: 520, lineHeight: 1.65 }}>
              OneBotAds ships native Model Context Protocol servers, so Claude, ChatGPT or your own agents can list, create and optimise campaigns programmatically.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 40, alignItems: 'center' }}>
            <McpTerminal active={visible} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {MCP_SERVERS.map((srv, i) => (
                <div key={i} className="mcp-card" style={{ padding: '16px 18px', borderRadius: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: srv.color, boxShadow: `0 0 8px ${srv.color}`, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'Consolas','Monaco',monospace", fontSize: 14, fontWeight: 700, color: '#fff' }}>{srv.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 999, background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.3)', color: '#818CF8' }}>6 TOOLS</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {srv.tools.map((t, j) => (
                      <span key={j} style={{ fontFamily: "'Consolas','Monaco',monospace", fontSize: 10.5, padding: '3px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AIShowcase;
